import { createKeyedListenerManager } from 'utility/KeyedListenerManager';
import { MultiScreenSession } from '../communication/MultiScreenSession';
import { MultiScreenBase } from './MultiScreenBase';

export class MultiScreenController extends MultiScreenBase {
    readonly request: PresentationRequest;

    private _releaseConnectionMonitor?: () => void;

    private _available: boolean;
    get available() { return this._available; }

    constructor() {
        super();

        const presentationRequest = new PresentationRequest(window.location.href);
        this.request = presentationRequest;

        this._available = false;
        this._startAvailabilityMonitor(); // intentionally non-awaited
        this._startAvailableConnectionMonitor(); // intentionally non-awaited
    }

    private async _startAvailableConnectionMonitor() {
        this.request.onconnectionavailable = event => {
            this.setupSession(event.connection);
        };
    }

    async start(): Promise<boolean> {
        if (!this.available) return false;

        try {
            const sessions = [...this.sessions.values()];
            await Promise.all(sessions.map(session => session.disconnect()));
            await this.request.start();
            await Promise.all(sessions.map(session => this.reconnect(session)));
            return true;
        } catch (e) {
            // in Chrome, there is only one way to open selection panel with the following error
            if (e instanceof DOMException && e.message === 'A presentation has already started.') {
                return true;
            }

            this._lastError = e;
            this._onError(e);
            return false;
        }
    }

    async stop(): Promise<void> {
        if (this._releaseConnectionMonitor) {
            this._releaseConnectionMonitor();
            this._releaseConnectionMonitor = undefined;
        }

        this.sessions.clear();
    }

    async reconnect(session: MultiScreenSession) {
        const updated = await this.request.reconnect(session.presentationId);
        this.setupSession(updated);
    }

    private _availabilityListenerManager = createKeyedListenerManager<string, () => void>();

    addEventListener(name: string, listener: () => void) {
        this._availabilityListenerManager.addListener(name, listener);
    }

    removeEventListener(name: string, listener: () => void) {
        this._availabilityListenerManager.removeListener(name, listener);
    }

    private async _startAvailabilityMonitor() {
        try {
            const availability = await this.request.getAvailability();
            this._available = availability.value;
            this._availabilityListenerManager.forEach('available', cb => cb());

            availability.onchange = () => {
                this._available = availability.value;
                this._availabilityListenerManager.forEach('available', cb => cb());
            };
        } catch (e) {
            this._lastError = e;
            this._onError(e);
        }
    }
}
